import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock } from "lucide-react";
import { useCoachToday } from "@/lib/coachQueries";

const SOURCE_LABEL = {
  feedback_request: "Feedback request", file_upload: "File upload", practice_no_match: "Practice — no match",
  message: "Message", session_prep: "Session prep", session_incomplete: "Incomplete session record",
  client_no_next_session: "No next session booked", review_submitted: "Review / testimonial",
  audit_import_awaiting_review: "Audit import", signature_error: "Signature error",
  recommendation_pending_review: "Recommendation ready for review",
  session_pending_calendar_sync: "Confirm on your calendar",
};

const PRIORITY_STYLE = {
  urgent: "bg-red-100 text-red-700 border-red-200", high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200", low: "bg-ink/5 text-ink-soft border-ink/10",
};

/**
 * Master v5 Prompt 16 — Coach Today. Tasks arrive already sorted by
 * the backend (overdue first, then priority, then soonest due) — this
 * page renders that order as-is rather than re-sorting client-side.
 * Clicking a row navigates to its exact `deep_link`; that navigation
 * alone never completes the task (see coach_task_repository.py) —
 * only the resolving action (publish/reply/recap/etc.) does.
 */
export default function CoachTodayPage() {
  const { data, isPending, isError } = useCoachToday();
  const navigate = useNavigate();
  const items = data?.items || [];

  return (
    <div data-testid="coach-today-page">
      <h1 className="font-serif text-2xl text-ink mb-1">Today</h1>
      <p className="text-sm text-ink-soft mb-6">
        {data?.coach_timezone ? `${data.coach_timezone.replace(/_/g, " ")} · ${new Date().toLocaleDateString("en-US", { timeZone: data.coach_timezone, weekday: "long", month: "long", day: "numeric" })}` : ""}
      </p>

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="coach-today-loading">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="coach-today-error">Couldn't load your task queue.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coach-today-empty">Nothing needs your attention right now.</p>
      ) : (
        <div className="space-y-2 max-w-3xl" data-testid="coach-today-list">
          {items.map((task) => (
            <button
              key={task.ctk_id}
              onClick={() => navigate(task.deep_link)}
              className={`w-full text-left border rounded-lg p-4 flex items-center gap-3 transition-colors hover:bg-ink/5 ${task.is_overdue ? "border-red-300 bg-red-50/50" : "border-ink/15 bg-white/50"}`}
              data-testid={`coach-today-task-${task.ctk_id}`}
            >
              <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${PRIORITY_STYLE[task.priority]}`} data-testid={`coach-today-task-priority-${task.ctk_id}`}>
                {task.priority}
              </span>
              <span
                className="shrink-0 text-[10px] font-medium uppercase tracking-wide border border-ink/15 rounded-full px-2 py-0.5 text-ink-soft"
                data-testid={`coach-today-task-scope-${task.ctk_id}`}
              >
                {task.scope === "client" ? task.member_name || "Client" : "Team"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink font-medium truncate">{task.reason}</p>
                <p className="text-xs text-ink-soft truncate">
                  {SOURCE_LABEL[task.source_type] || task.source_type} · {task.workspace_title}
                </p>
              </div>
              {task.due_at && (
                <span className={`shrink-0 text-xs flex items-center gap-1 ${task.is_overdue ? "text-red-600 font-medium" : "text-ink-soft"}`} data-testid={`coach-today-task-due-${task.ctk_id}`}>
                  {task.is_overdue ? <AlertTriangle size={12} /> : <Clock size={12} />}
                  {new Date(task.due_at).toLocaleString("en-US", { timeZone: data?.coach_timezone || "America/Sao_Paulo", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
